/**
 * Simple page data cache using sessionStorage.
 * Used to restore page state when navigating back.
 * Cache is automatically cleared on page refresh.
 */

const CACHE_PREFIX = 'pageCache_';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

/**
 * Clear all page cache data
 */
export function clearAllPageCache(): void {
  const keysToRemove: string[] = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key?.startsWith(CACHE_PREFIX)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => sessionStorage.removeItem(key));
}

/**
 * Initialize cache - clears cache on page refresh
 * Call this once at app startup
 */
export function initializePageCache(): void {
  // Check if this is a page reload using Performance API
  const navEntries = performance.getEntriesByType(
    'navigation'
  ) as PerformanceNavigationTiming[];
  const isReload = navEntries.length > 0 && navEntries[0].type === 'reload';

  if (isReload) {
    clearAllPageCache();
  }
}

export function getCachedData<T>(key: string): T | null {
  try {
    const cached = sessionStorage.getItem(CACHE_PREFIX + key);
    if (!cached) return null;

    const entry: CacheEntry<T> = JSON.parse(cached);
    const age = Date.now() - entry.timestamp;

    // Return cached data if not expired
    if (age < CACHE_TTL) {
      return entry.data;
    }

    // Clear expired cache
    sessionStorage.removeItem(CACHE_PREFIX + key);
    return null;
  } catch {
    return null;
  }
}

export function setCachedData<T>(key: string, data: T): void {
  try {
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
    };
    sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify(entry));
  } catch {
    // Ignore storage errors (quota exceeded, etc.)
  }
}

export function clearCachedData(key: string): void {
  sessionStorage.removeItem(CACHE_PREFIX + key);
}

/**
 * Clear all cached data for a given prefix (e.g., 'parts' or 'fulfillment')
 */
export function clearAllCachedDataFor(prefix: string): void {
  const keysToRemove: string[] = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key?.startsWith(CACHE_PREFIX + prefix)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => sessionStorage.removeItem(key));
}
